import { PageHeader, cn } from '@ezhire/ui';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface GroupHubPage {
  /** Display label on the card. */
  label: string;
  /** One-line description shown below the label. */
  description: string;
  /** Absolute path to navigate to. */
  to: string;
  /**
   * Optional search params merged into the navigation (e.g. `{ tab: 'matrix' }` for a
   * Workspace hub whose sub-pages are `?tab=` dispatched, not separate routes).
   */
  search?: Record<string, unknown>;
  /** Lucide icon component. */
  icon: LucideIcon;
  /** Optional phase marker (v1.5, v2, etc.) — shown as a muted badge on the card. */
  phase?: string;
}

interface GroupHubProps {
  /** Omit (along with `subtitle`) when an ancestor already renders the page's
   *  title — e.g. a Workspace's Overview tab, where `ModuleWorkspaceTabs`
   *  owns the `<PageHeader>` and a second one here would duplicate it. */
  title?: string;
  subtitle?: string;
  pages: GroupHubPage[];
}

/**
 * Generic group-landing hub used by module parent nav items that have
 * children. Renders a card grid so users can navigate to sub-pages
 * directly from the parent item's route, in addition to the sidebar tree.
 *
 * Usage:
 *   import { GroupHub } from '@ezhire/sdk';
 *   <GroupHub title="Google Ads" subtitle="..." pages={[...]} />
 */
export function GroupHub({ title, subtitle, pages }: GroupHubProps) {
  return (
    <div>
      {title && <PageHeader title={title} subtitle={subtitle} />}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pages.map((page) => (
          <Link
            key={page.to + (page.search ? JSON.stringify(page.search) : '')}
            to={page.to}
            // Functional merge, not a replacement object — a plain object here
            // would DROP the current search state (e.g. Overview's own
            // market/productType filters) instead of layering `page.search`
            // on top of it (Codex P2 on the RFM R5 review). Only merge when this
            // page actually declares search params — every other GroupHub consumer
            // (marketing/cx/insights/finops/finance-close/hr) leaves `search`
            // undefined and must keep the prior no-inherit behavior, not start
            // carrying the source route's search state into unrelated subpages.
            search={page.search ? (((prev: Record<string, unknown>) => ({ ...prev, ...page.search })) as never) : undefined}
            className={cn(
              'group flex flex-col gap-4 rounded-xl border border-border bg-bg-surface p-5',
              'hover:border-accent-500/40 hover:bg-bg-elevated',
              'transition-all duration-150',
              page.phase && 'opacity-60',
            )}
          >
            {/* Icon badge + optional phase pill */}
            <div className="flex items-center justify-between">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-500/10 transition-colors group-hover:bg-accent-500/15">
                <page.icon className="h-5 w-5 text-accent-500" />
              </div>
              {page.phase && (
                <span className="rounded-full border border-border bg-bg-elevated px-2 py-0.5 font-mono text-[10px] text-text-muted">
                  {page.phase}
                </span>
              )}
            </div>

            {/* Label + description */}
            <div className="flex-1">
              <p className="text-body font-medium text-text-primary">{page.label}</p>
              <p className="mt-1 text-caption text-text-muted">{page.description}</p>
            </div>

            {/* Open arrow */}
            <div className="flex items-center gap-0.5 text-caption text-text-muted transition-colors group-hover:text-accent-500">
              <span>{page.phase ? 'Coming soon' : 'Open'}</span>
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
